// RouteFocus — a11y companion to ScrollToTop/DocumentTitle for SPA
// navigations.
//
// Wouter route changes move keyboard focus nowhere and announce nothing
// to assistive tech, so a screen-reader user who activates a nav link
// hears silence and their reading position stays on the old page. This
// component closes both gaps on every location change:
//
//   1. Moves focus to the <main id="main-content" tabIndex={-1}> wrapper
//      in App.tsx so keyboard and screen-reader reading position starts
//      at the top of the new page's content.
//   2. Announces the new route through a polite live region, using the
//      same per-route titles the prerender injects and DocumentTitle
//      sets (src/data/route-titles.ts).
//
// The initial page load is deliberately skipped: the browser already
// places focus at the document start, and the screen reader announces
// the document title on load — announcing again would double up.

import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { titleForPath } from "./DocumentTitle";

export default function RouteFocus() {
  const [location] = useLocation();
  const [announcement, setAnnouncement] = useState("");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const main = document.getElementById("main-content");
    if (main) {
      // preventScroll: ScrollToTop owns scroll position (top-of-page or
      // hash anchor) — focus must not fight it.
      main.focus({ preventScroll: true });
    }
    setAnnouncement(titleForPath(location));
  }, [location]);

  return (
    <div aria-live="polite" role="status" className="visually-hidden">
      {announcement}
    </div>
  );
}
